<template>
    <article class="amPreview" :class="containerClass">
        <div class="amPreview__container" :style="previewStyle">
            <button type="button" class="amPreview__empty" @click="onList">
                SELECT A PREVIEW COMPONENT
            </button>

            <div class="amPreview__group">
                <ul class="amPreview__list">
                    <li v-for="element in elementList" :key="element.elementType" class="amPreview__item">
                        <button class="amPreview__button" @click="onSelect(element)">
                            {{ element.displayName }}
                        </button>
                    </li>
                </ul>

                <ul class="amPreview__list">
                    <li v-for="component in componentList" :key="component.id" class="amPreview__item">
                        <button class="amPreview__button" @click="onSelect(component)">
                            {{ component.displayName }}
                        </button>
                    </li>
                </ul>
            </div>

            <div class="amPreview__edit">
                <div class="amPreview__controls">
                    <button class="amPreview__control amPreview__control--play" type="button" @click="onReset">
                        <font-awesome-icon :icon="['fas', 'redo']" />
                    </button>

                    <button class="amPreview__control amPreview__control--play" type="button" @click="onPlay">
                        <font-awesome-icon :icon="['fas', 'play']" />
                    </button>

                    <button class="amPreview__control" type="button">
                        <font-awesome-icon :icon="['fas', 'pause']" @click="onPause" />
                    </button>

                    <button class="amPreview__control" type="button">
                        <font-awesome-icon :icon="['fas', 'backward']" @click="onReverse" />
                    </button>

                    <button class="amPreview__control amPreview__control--minimize" type="button" @click="onMinimize">
                        <font-awesome-icon :icon="['fal', 'window-minimize']" />
                    </button>

                    <button class="amPreview__control" type="button" @click="onMaximize">
                        <font-awesome-icon :icon="['fal', 'window-maximize']" />
                    </button>

                    <button class="amPreview__control amPreview__control--exit" type="button" @click="onExit">
                        <font-awesome-icon :icon="['fal', 'times']" />
                    </button>
                </div>

                <section class="amPreview__component">
                    <loading-component v-if="loading" />

                    <div class="amPreview__component-container">
                        <component
                            :is="selectedComponent.elementType"
                            v-if="selectedComponent && !loading"
                            ref="selectedComponent"
                            :top-level="true"
                            v-bind="selectedComponent"
                        />
                    </div>
                </section>
            </div>
        </div>
    </article>
</template>

<script>
import { mapState } from 'vuex';

import LoadingComponent from '@/components/shared/loading/loading-component';
import { tweenVarPropMap } from '@/data/animations/tween-vars';
import { fetchRendering } from '@/utils/rendering';
import { COMPONENT_TREE_SECTIONS } from '@/data/constants/component-tree-sections';
import { STATE_CLASSES } from '@/data/constants/classes';

const modes = {
    DEFAULT: 'default',
    SELECT: 'select',
    DISPLAY: 'edit',
};

const displayModes = {
    DEFAULT: 'default',
    MAXIMIZED: 'maximized',
};

export default {
    name: 'animation-preview',
    components: { LoadingComponent },

    props: {
        animation: {
            type: Object,
            default: () => null,
        },

        isActive: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            loading: false,
            mode: modes.DEFAULT,
            displayMode: displayModes.DEFAULT,
            previewElements: null,
            selectedComponent: null,
        };
    },

    computed: {
        ...mapState({
            componentTree: state => state.page.componentTree,
            selectedElements: state => state.page.selectedElements,
        }),

        elementList() {
            return [
                { elementType: 'a-button', displayName: 'Button' },
                { elementType: 'a-link', displayName: 'Link' },
                { elementType: 'a-heading', displayName: 'Heading' },
                { elementType: 'a-text-multi-line', displayName: 'Paragraph' },
                { elementType: 'shape', displayName: 'Box' },
            ];
        },

        componentList() {
            return this.componentTree
                .filter((section) => {
                    return section.name === COMPONENT_TREE_SECTIONS.CLIENT || section.name === COMPONENT_TREE_SECTIONS.SHARED;
                })
                .flatMap((section) => {
                    return section.items.flatMap(item => item.children);
                })
                .sort((a, b) => {
                    return a.displayName < b.displayName ? -1 : 1;
                });
        },

        containerClass() {
            return {
                [STATE_CLASSES.ENABLED]: this.mode === modes.DISPLAY,
                [STATE_CLASSES.EXPANDED]: this.displayMode === displayModes.MAXIMIZED,
            };
        },

        previewStyle() {
            let transform;

            switch (this.mode) {
                case modes.DEFAULT:
                    transform = 'translateY(0%)';
                    break;

                case modes.SELECT:
                    transform = 'translateY(-100%)';
                    break;

                case modes.DISPLAY:
                    transform = 'translateY(-200%)';
                    break;

                default:
                    // do nothing
            }

            return { transform };
        },
    },

    watch: {
        animation: {
            handler() {
                this.onReset();
                this.initAnimation();
            },
            deep: true,
        },

        isActive() {
            if (this.selectedComponent) {
                this.onReset();
                this.initAnimation();
            }
        },

        previewElements() {
            this.onReset();
            this.initAnimation();
        },

        async selectedComponent() {
            await this.$nextTick();
            await this.$nextTick();

            this.previewElements = [this.$refs.selectedComponent.$el];
        },

        selectedElements() {
            if (this.selectedElements.length) {
                this.previewElements = this.selectedElements.map(elem => elem.$el);
            } else {
                this.previewElements = [this.$refs.selectedComponent.$el];
            }
        },
    },

    methods: {
        async initAnimation() {
            const { tweens, repeat, repeatDelay, repeatAlternate } = this.animation;

            const { gsap } = await import('gsap');

            this.timeline = gsap.timeline({ paused: true, repeat, repeatDelay });
            this.timeline.yoyo(repeatAlternate);

            tweens.forEach((tween) => {
                const combinedVars = {};

                tween.vars.forEach((tweenVar) => {
                    const prop = tweenVarPropMap[tweenVar.prop];

                    combinedVars[prop] = `${ tweenVar.value }${ tweenVar.unit }`;
                });

                this.timeline.to(this.previewElements, {
                    ...combinedVars,
                    duration: tween.duration,
                    delay: tween.delay,
                    ease: tween.ease,
                });
            });
        },

        onPlay() {
            this.timeline.play();
        },

        onPause() {
            this.timeline.pause();
        },

        onReverse() {
            this.timeline.reverse();
        },

        onReset() {
            if (!this.timeline) return;

            this.timeline.seek(0);
            this.timeline.pause();
        },

        onMaximize() {
            this.displayMode = displayModes.MAXIMIZED;
        },

        onMinimize() {
            this.displayMode = displayModes.DEFAULT;
        },

        onList() {
            this.mode = modes.SELECT;
            this.displayMode = displayModes.DEFAULT;
        },

        onExit() {
            this.mode = modes.SELECT;
            this.displayMode = displayModes.DEFAULT;

            window.setTimeout(this.onReset, 500);
        },

        onSelect(component) {
            this.mode = modes.DISPLAY;

            if (component.props) {
                this.loadComponent(component.props.componentItemID);
            } else {
                this.loadElement(component);
            }
        },

        loadComponent(componentItemID) {
            this.loading = true;

            // the slight delay allows the coponent pane to slide up smoothly without freezing up halfway
            window.setTimeout(async () => {
                const rendering = await fetchRendering(componentItemID);

                if (!rendering) {
                    this.loading = false;
                    this.mode = modes.SELECT;
                    return;
                }

                this.loading = false;
                this.selectedComponent = rendering;
            }, 500);
        },

        loadElement(element) {
            this.selectedComponent = element;
        },
    },
};
</script>

<style lang="scss">
.amPreview {
    $this: &;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 100%;
    min-height: 25rem;
    color: var(--artboard-color);
    background-color: var(--artboard-backgroundColor);
    border: 5px solid var(--elementToolbar-backgroundColor);
    padding: 2em;
    overflow: hidden;
    transition: all 0.35s 0.25s;

    &.#{ $is-enabled } {
        border-top: none;
    }

    &.#{ $is-expanded } {
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 1000;
    }

    .#{ $is-selected-element } {
        &:hover {
            outline: 2px dashed var(--elementToolbar-backgroundColor) !important;
        }
    }

    &__container {
        @extend %fill;
        display: flex;
        flex-direction: column;
        transition: transform 0.25s;
    }

    &__empty {
        min-height: 100%;
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        transition: background-color 0.35s;
        font-size: 1.25rem;
        font-weight: 700;
        color: darkgray;
        background-color: var(--modal-backgroundColor);

        &:hover {
            background-color: var(--modal-hoverBackgroundColor);
        }
    }

    &__group {
        min-height: 100%;
        overflow: auto;
        color: var(--modal-color);
        background-color: var(--modal-backgroundColor);
    }

    &__list {
        display: flex;
        flex-wrap: wrap;

        &:not(:first-of-type) {
            border-top: 10px solid var(--app-borderColor);
            margin-top: 50px;
        }

        &:not(:last-of-type) {
            border-bottom: 10px solid var(--app-borderColor);
        }
    }

    &__item {
        border: 1px solid var(--app-borderColor);
        width: 20%;
    }

    &__button {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
        padding: 1em;
        min-height: 5em;
        font-weight: 700;
        text-transform: uppercase;
        color: darkgray;

        #{ $this }__item:nth-of-type(even) & {
            background-color: var(--app-hoverBackgroundColor);
        }

        #{ $this }__item:hover & {
            color: white;
            background-color: var(--form-buttonBackgroundColor);
        }
    }

    &__edit {
        position: relative;
        min-height: 100%;
    }

    &__controls {
        display: flex;
        justify-content: flex-end;
        position: absolute;
        right: 0;
        left: 0;
        z-index: 100;
        color: var(--elementToolbar-color);
        background-color: var(--elementToolbar-backgroundColor);
    }

    &__control {
        display: flex;
        justify-content: center;
        transition: all 0.25s;
        padding: 0.5em 0.75em 0.375em;
        font-size: 1rem;

        &--play {
            font-size: 0.875em;
        }

        &--minimize {
            margin-left: auto;
        }

        &--exit {
            font-size: 1.375em;
        }

        &:hover {
            color: var(--elementToolbar-hoverColor);
            background-color: var(--elementToolbar-hoverBackgroundColor);
        }
    }

    &__component {
        @extend %fill;
        display: flex;
        overflow: auto;

        *:hover {
            outline: none !important;
            cursor: pointer;
        }
    }

    &__component-container {
        margin: auto;
    }
}
</style>
